import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";

import { replaceFile as replaceFileApi, uploadComplete } from "../api/files.api";
import type { StorageErrorCode } from "../types/project-storage.types";
import type { UploadQueueItem } from "../types/upload-queue.types";
import { putToS3 } from "../utils/putToS3";
import { getStorageErrorMessage, mapStorageError } from "../utils/storageErrors";
import { mapCompleteResult } from "../utils/uploadFlow";
import { folderChildrenQueryKey } from "./useFolderChildren";
import { folderTreeQueryKey } from "./useFolderTree";

export type ReplaceFileResult =
  | { ok: true }
  | { ok: false; errorCode?: StorageErrorCode; errorMessage: string };

export function useReplaceFile(mmCode: number | null, folderId: string | null) {
  const queryClient = useQueryClient();
  const [replacingItemId, setReplacingItemId] = useState<string | null>(null);

  const invalidateFolderData = useCallback(async () => {
    if (mmCode === null || !folderId) return;

    await Promise.all([
      queryClient.invalidateQueries(folderChildrenQueryKey(mmCode, folderId)),
      queryClient.invalidateQueries(folderTreeQueryKey(mmCode)),
    ]);
  }, [folderId, mmCode, queryClient]);

  const replaceConflictFile = useCallback(
    async (item: UploadQueueItem): Promise<ReplaceFileResult> => {
      if (mmCode === null || !item.existingFileId) {
        return {
          ok: false,
          errorMessage: "Não foi possível substituir o arquivo.",
        };
      }

      setReplacingItemId(item.id);

      try {
        const replaceInit = await replaceFileApi(mmCode, item.existingFileId, {
          displayName: item.displayName,
          mimeType: item.mimeType,
          sizeBytes: item.sizeBytes,
        });

        const putOk = await putToS3(
          replaceInit.presignedUrl,
          item.mimeType,
          item.file
        );

        if (!putOk) {
          return {
            ok: false,
            errorMessage: getStorageErrorMessage("FILE_NOT_FOUND_IN_STORAGE"),
          };
        }

        const completeResponse = await uploadComplete(mmCode, {
          fileIds: [replaceInit.fileId],
        });

        const completeResult = completeResponse.results.find(
          (result) => result.fileId === replaceInit.fileId
        );

        if (!completeResult) {
          return {
            ok: false,
            errorMessage: getStorageErrorMessage("FILE_NOT_FOUND_IN_STORAGE"),
          };
        }

        const mapped = mapCompleteResult(completeResult);
        if (mapped.ok) {
          await invalidateFolderData();
          return { ok: true };
        }

        return {
          ok: false,
          errorCode: mapped.errorCode,
          errorMessage: getStorageErrorMessage(mapped.errorCode),
        };
      } catch (error) {
        const mapped = mapStorageError(error, "upload");
        return {
          ok: false,
          errorCode: mapped.errorCode,
          errorMessage: mapped.message,
        };
      } finally {
        setReplacingItemId(null);
      }
    },
    [invalidateFolderData, mmCode]
  );

  return {
    replaceConflictFile,
    replacingItemId,
    isReplacing: replacingItemId !== null,
  };
}
